import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { registerSW } from 'virtual:pwa-register'

/**
 * Service worker registration and the update signal (T-204).
 *
 * A provider rather than a plain hook, because registration must happen
 * **once**. Two components each calling `registerSW` would install two update
 * listeners and race to reload, and the second registration is exactly the kind
 * of thing that works in development and misbehaves once a real deploy lands.
 *
 * Registration lives here rather than in the plugin's `injectRegister` so the
 * update state is React state — which is what lets the prompt be deferred while
 * an editor is dirty rather than firing whenever Workbox happens to notice.
 */
export interface ServiceWorkerState {
  /** A new version is downloaded and waiting to take over. */
  readonly updateReady: boolean
  /** The app is precached and will start with no network. */
  readonly offlineReady: boolean
  /** Activates the waiting worker and reloads — exactly once. */
  readonly applyUpdate: () => void
}

const ServiceWorkerContext = createContext<ServiceWorkerState | null>(null)

export function ServiceWorkerProvider({ children }: { readonly children: ReactNode }) {
  const [updateReady, setUpdateReady] = useState(false)
  const [offlineReady, setOfflineReady] = useState(false)
  const update = useRef<((reload?: boolean) => Promise<void>) | null>(null)

  useEffect(() => {
    update.current = registerSW({
      immediate: true,
      onNeedRefresh: () => setUpdateReady(true),
      onOfflineReady: () => setOfflineReady(true),
    })
  }, [])

  const applyUpdate = useCallback(() => {
    // Cleared first, so a double tap finds nothing waiting. Reloading twice is
    // harmless but reads as a fault, and this is the exact moment the user is
    // deciding whether the app is reliable.
    setUpdateReady(false)
    void update.current?.(true)
  }, [])

  const value = useMemo(
    () => ({ updateReady, offlineReady, applyUpdate }),
    [updateReady, offlineReady, applyUpdate],
  )

  return (
    <ServiceWorkerContext.Provider value={value}>
      {children}
    </ServiceWorkerContext.Provider>
  )
}

export function useServiceWorker(): ServiceWorkerState {
  const state = useContext(ServiceWorkerContext)
  if (!state) {
    throw new Error('useServiceWorker must be used inside a <ServiceWorkerProvider>')
  }
  return state
}
